import SyntaxHighlighter from "react-syntax-highlighter"

/* eslint-disable @next/next/no-img-element */
import { HoverSkew } from "app/splash/components/HoverSkew"
import { SIcon } from "@superwall-me/sui"
import { ColorBlur } from "app/splash/components/ColorBlur"
import { ReactNode } from "react"
import { Options, documentToReactComponents } from "@contentful/rich-text-react-renderer"
import { BLOCKS, MARKS, Document } from "@contentful/rich-text-types"
import React from "react"
import { Block, Inline } from "@contentful/rich-text-types"
import Link from "next/link"
import { fixContentfulUrl } from "integrations/contentful"
import { theme } from "./SHTheme"
import { CopyButton } from "app/core/components/Misc"

const selectWithCodeWrapper = (content: Document): Document => {
  const nodes = content.content
  try {
    for (let i = nodes.length - 1; i > -1; i--) {
      if (
        nodes[i] &&
        nodes[i - 1] &&
        nodes[i]!.nodeType === BLOCKS.PARAGRAPH &&
        (nodes[i]!.content[0] as any)?.marks?.[0]?.type === MARKS.CODE &&
        (nodes[i - 1]!.content[0] as any)?.marks?.[0]?.type === MARKS.CODE
      ) {
        ;(nodes[i - 1]!.content[0] as any).value += `\r` + (nodes[i]?.content[0] as any).value
        delete nodes[i]
      }
    }
    return {
      ...content,
      content: nodes,
    }
  } catch (e) {
    console.error(e)
  }
  return content
}

const combineCodeMarksIntoOne = (document: Document) => {
  const nodes = document.content

  let outputNodes: typeof nodes = []
  for (let i = 0; i < nodes.length; i++) {
    console.log(`!! node[i]`, i, nodes[i], nodes.length)
    const node = nodes[i]!
    if (!node) {
      continue
    }
    if (node.nodeType === BLOCKS.PARAGRAPH) {
      const children = node.content
      if (children.every((c) => c.nodeType === "text" && c.marks.some((m) => m.type === "code"))) {
        // The whole paragraph is code, we should render it as such
        outputNodes.push({
          ...node,
          data: {
            ...node.data,
            code: children.map((c) => (c as any).value).join(""),
          },
        })
        continue
      }
    }
    outputNodes.push(node)
  }

  return {
    ...document,
    content: outputNodes,
  }
}

function parseInput(input: string) {
  // Split the input based on '`', keeping track of alternating between text and code
  const parts = input.split("`")
  const result = parts.map((part, index) => {
    // Determine if the current part is code or text based on its position
    const type = index % 2 === 0 ? "text" : "code"
    return { type, value: part }
  })

  // Optionally, filter out empty strings if needed
  return result.filter((part) => part.value !== "")
}

// export const Markdown = ({ markdown }: { markdown: string }) => {
// const result = useMemo(
//   () =>
//     remark()
//       .use(html as any)
//       .processSync(markdown)
//       .toString(),
//   [markdown]
// )
// return <article className="prose" dangerouslySetInnerHTML={{ __html: result }} />
// }

export const RichText = ({ document, classNames }: { document: any; classNames?: string }) => {
  const options: Options = {
    renderText: (text) => {
      const parsed = parseInput(text)

      return parsed.map(({ type, value }, index) => {
        if (type === "text") {
          return <>{value}</>
        }
        return (
          <span key={index}>
            {" "}
            <code>{value}</code>{" "}
          </span>
        )
      })
    },
    renderMark: {
      [MARKS.CODE]: (text: string) => {
        return (
          // prevents including leading/trailing whitespace in a code block by mistake
          <span>
            {"   "}
            <code
              className={
                "break-words md:break-normal text-[0.925rem] font-normal !mx-0 bg-brand-primary/10"
              }
            >
              {typeof text === "string" ? text?.trim() : text}
            </code>
          </span>
        )
      },
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => {
        if (node.data.code) {
          return (
            <div className="not-prose !text-[1rem] p-[10px] pb-[0] border border-white/10 rounded-2xl bg-[rgba(68,73,78,0.1)] my-5">
              <SyntaxHighlighter language={node.data.language} style={theme}>
                {node.data.code.trim()}
              </SyntaxHighlighter>
              <div className="w-full pl-[10px] py-1 flex flex-row items-center justify-between ">
                <p className=" opacity-40 ">{node.data.language}</p>
                <CopyButton copiableText={node.data.code} title={"Copy"} />
              </div>
            </div>
          )
        }
        return <p>{children}</p>
      },
      [BLOCKS.DOCUMENT]: (node, children) => (
        <article className={`prose ${classNames}`}>{children}</article>
      ),
      [BLOCKS.HEADING_1]: (node, children) => <h1>{children}</h1>,
      [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
        const { nodeType } = node
        const { fields } = node.data.target

        if (nodeType === "embedded-entry-block" && fields) {
          const { id: entryType } = node.data.target.sys.contentType.sys
          const { title, subtitle, slug, url, image, siteName, favicon } = node.data.target.fields
          const href = slug ? `/blog/${slug}` : url
          let description = subtitle || href || ""
          // truncate description if it's too long
          if (description.length > 160) {
            description = description.substring(0, 160) + "..."
          }

          switch (entryType) {
            case "post":
              return (
                <HoverSkew
                  glowStength="light"
                  clickable={true}
                  borderRadius="15px"
                  className={`group hover:border-white/20 transition flex-shrink self-shrink relative ${
                    image ? "p-[8px]" : "p-4 px-2 md:p-5 md:px-3"
                  } my-3`}
                >
                  <Link
                    target="_blank"
                    passHref={true}
                    href={href}
                    className="block"
                    style={{ textDecoration: "none", color: "white", fontWeight: "normal" }}
                  >
                    <div className="grid grid-cols-[1fr,auto] space-between gap-0 ">
                      <div className="flex flex-col sm:grid sm:grid-cols-[auto,1fr] gap-4 sm:gap-2">
                        {image && (
                          <img
                            className="rounded-[10px] object-cover w-full sm:h-[140px] sm:w-[240px] w-auto ring-1 ring-white/10 shadow-xl w-full h-auto my-0 mr-1"
                            src={`${fixContentfulUrl(image.fields.file.url)}`}
                            alt={image.title}
                          />
                        )}

                        <div className="grid grid-cols-[1fr,auto] space-between gap-0">
                          <div className="flex flex-col items-start justify-center">
                            <div className="transition group-hover:text-brand-primary mb-0 mt-0 px-2 text-lg font-medium">
                              <h4 className="mt-0 mb-2 mr-6">{title}</h4>
                            </div>

                            <div className="transition group-hover:text-brand-primary/90 mb-0 mt-0 px-2 text-base text-white/60">
                              <p className="my-0 text-white/60">{description}</p>
                            </div>
                          </div>
                          <div className="flex absolute z-1 top-1 right-1 items-center justify-center p-2">
                            <SIcon
                              size={18}
                              className="text-white/30 transition group-hover:text-brand-primary transform duration-200 group-hover:scale-[1.1]"
                              type="open"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </HoverSkew>
              )
          }
        } else {
          return <></>
        }

        return <></>
      },
      [BLOCKS.EMBEDDED_RESOURCE]: (node, children) => {
        return (
          <span>
            <code>Waring</code> Embedded assets are not supported
          </span>
        )
      },
      [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
        const { description, title, file } = node.data.target.fields
        const hasDescription = description && description !== ""
        const containerPadding = "md:p-[10px]"
        const imageRadius = hasDescription
          ? "md:rounded-[10px] rounded-tr-[13px] rounded-tl-[13px]"
          : "rounded-[13px]"
        const isVideo = file.contentType.includes("video")
        const isImage = file.contentType.includes("image")
        const isPDF = file.contentType.includes("pdf")
        const isCSV = file.contentType.includes("csv")

        if (isVideo) {
          return (
            <div className="relative flex flex-col items-center justify-center my-5">
              {/* <div className="absolute z-[0]"> */}
              <ColorBlur opacity={0.15} />
              {/* </div> */}

              <video
                className={`py-0 m-0 relative z-1 ring-1 ring-white/10 shadow-xl rounded-[13px] ${imageRadius}`}
                src={fixContentfulUrl(file.url)}
                loop
                autoPlay
                controls={true}
              />
              {description && (
                <p className="text-center mb-0 mt-3 text-white/60 px-2 text-base">{description}</p>
              )}
            </div>
          )
        }

        if (isImage) {
          return (
            <div className="relative flex flex-col items-center justify-center my-5">
              <ColorBlur opacity={0.15} />
              <div>
                <HoverSkew
                  glowStength="light"
                  clickable={true}
                  borderRadius="20px"
                  className={`flex-shrink hover:border-white/20 transition  self-shrink ${containerPadding}`}
                >
                  <Link
                    target="_blank"
                    passHref={true}
                    href={`${fixContentfulUrl(file.url)}`}
                    className="block mx-0"
                  >
                    <img
                      className={`py-0 m-0  mx-auto md:max-h-[700px] object-cover ring-1 ring-white/10 shadow-xl ${imageRadius}`}
                      src={fixContentfulUrl(file.url)}
                      alt={file.title}
                    />
                  </Link>
                  {description && (
                    <p className="mx-auto text-center mb-0 mt-[7px] text-white/60 pb-3 md:pb-0 py-1 px-3 text-base">
                      {description}
                    </p>
                  )}
                </HoverSkew>
              </div>
            </div>
          )
        }
        return (
          <InlineAttachment
            url={`${fixContentfulUrl(file.url)}`}
            title={title || "Download"}
            description={description || file.fileName}
            icon={<SIcon size={50} type={isPDF ? "pdf" : isCSV ? "csv" : "file"} />}
            accessory={
              <SIcon
                size={24}
                className="text-white/30 transition group-hover:text-brand-primary transform duration-200 group-hover:scale-[1.1]"
                type="download"
              />
            }
          />
        )
      },
    },
  }

  return <div className="text-lg">{documentToReactComponents(document, options)}</div>
}

const InlineAttachment = ({
  url,
  title,
  description,
  icon,
  accessory,
  containerClassName,
}: {
  url: string
  title?: ReactNode | string
  description?: ReactNode | string
  icon?: ReactNode
  accessory?: ReactNode
  containerClassName?: string
}) => {
  return (
    <HoverSkew
      glowStength="light"
      clickable={true}
      borderRadius="15px"
      className={`group hover:border-white/20 transition flex-shrink self-shrink p-5 my-3 ${containerClassName}`}
    >
      <Link
        target="_blank"
        passHref={true}
        href={url}
        className="block"
        style={{ textDecoration: "none", color: "white", fontWeight: "normal" }}
      >
        <div className="grid grid-cols-[1fr,auto] space-between gap-0">
          <div className="grid grid-cols-[auto,1fr] gap-2">
            {icon}

            <div className="flex flex-col items-start justify-center">
              {title && (
                <div className="transition group-hover:text-brand-primary mb-0 mt-0 px-2 text-lg font-medium">
                  {title}
                </div>
              )}

              {description && (
                <div className="transition group-hover:text-brand-primary/90 mb-0 mt-0 px-2 text-base text-white/60">
                  {description}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-center p-2 px-3">{accessory}</div>
        </div>
      </Link>
    </HoverSkew>
  )
}

type NodeRenderProps = {
  node: Block | Inline
  children?: ReactNode
}
